import { useState } from "react";
import { Download, Smartphone, Share, Plus, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useInstallApp } from "@/hooks/useInstallApp";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface InstallAppButtonProps {
  variant?: "header" | "hero";
  className?: string;
}

/**
 * Botão "Instalar App" — funciona em Android/Desktop via prompt nativo.
 * Em iOS, usa a Web Share API para abrir o menu de compartilhamento do Safari,
 * onde o usuário escolhe "Adicionar à Tela de Início". Se a API não estiver
 * disponível, mostra instruções visuais. Esconde-se sozinho se o app já estiver instalado.
 */
export const InstallAppButton = ({ variant = "header", className }: InstallAppButtonProps) => {
  const { canInstall, isIOS, install, openShare, canShare, hasNativePrompt } = useInstallApp();
  const [showIOS, setShowIOS] = useState(false);

  if (!canInstall) return null;

  const handleClick = async () => {
    if (hasNativePrompt) {
      const outcome = await install();
      if (outcome === "accepted") {
        toast({ title: "App instalado!", description: "Agora você pode abrir direto da tela inicial." });
      }
      return;
    }

    if (isIOS && canShare) {
      const shared = await openShare();
      if (!shared) setShowIOS(true);
      return;
    }

    if (isIOS) {
      setShowIOS(true);
    }
  };

  if (variant === "hero") {
    return (
      <>
        <button
          onClick={handleClick}
          className={cn(
            "group relative inline-flex h-12 items-center gap-3 overflow-hidden rounded-xl border border-[#0a1f44]/15 bg-white/80 px-6 font-semibold text-[#0a1f44] shadow-lg shadow-[#0a1f44]/10 backdrop-blur transition-all hover:-translate-y-0.5 hover:border-[#0a1f44]/30 hover:shadow-xl hover:shadow-[#0a1f44]/20 active:scale-[0.98]",
            className,
          )}
        >
          <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
          <Download className="relative h-5 w-5" strokeWidth={2.5} />
          <span className="relative">Instalar App</span>
          <span className="relative ml-1 rounded-full bg-[#047857]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#047857]">
            Grátis
          </span>
        </button>
        <IOSInstructionsDialog open={showIOS} onOpenChange={setShowIOS} />
      </>
    );
  }

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleClick}
        className={cn(
          "h-9 gap-1.5 rounded-lg border border-primary/20 bg-primary/5 px-3 text-xs font-semibold text-primary transition-all hover:border-primary/40 hover:bg-primary/10",
          className,
        )}
        aria-label="Instalar aplicativo"
      >
        <Download className="h-3.5 w-3.5" strokeWidth={2.5} />
        <span className="hidden sm:inline">Instalar</span>
      </Button>
      <IOSInstructionsDialog open={showIOS} onOpenChange={setShowIOS} />
    </>
  );
};

const IOSInstructionsDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
          <Smartphone className="h-6 w-6 text-primary" />
        </div>
        <DialogTitle className="text-center">Instalar no iPhone / iPad</DialogTitle>
        <DialogDescription className="text-center">
          Em apenas 3 passos rápidos
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3 pt-2">
        <Step
          n={1}
          icon={<Share className="h-5 w-5 text-blue-500" />}
          title="Toque no botão Compartilhar"
          desc="Ícone de seta para cima na barra inferior do Safari."
        />
        <Step
          n={2}
          icon={<Plus className="h-5 w-5 text-blue-500" />}
          title='Escolha "Adicionar à Tela de Início"'
          desc="Role para baixo se não aparecer logo."
        />
        <Step
          n={3}
          icon={<Check className="h-5 w-5 text-emerald-500" />}
          title='Toque em "Adicionar"'
          desc="O app aparecerá na sua tela inicial como um app normal."
        />
      </div>
    </DialogContent>
  </Dialog>
);

const Step = ({ n, icon, title, desc }: { n: number; icon: React.ReactNode; title: string; desc: string }) => (
  <div className="flex gap-3 rounded-xl border border-border bg-muted/30 p-3">
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
      {n}
    </div>
    <div className="flex-1">
      <div className="flex items-center gap-2">
        {icon}
        <p className="text-sm font-semibold">{title}</p>
      </div>
      <p className="mt-0.5 text-xs text-muted-foreground">{desc}</p>
    </div>
  </div>
);
